/**
 * Slash command catalog — Phases 2 & 3.
 *
 * Tier A (deterministic, no LLM): pause, resume, budget, topup, launch,
 *   new, stop, btw, help, whoami, go, campaign, brand, admin
 *
 * Tier B (LLM-routed): perf, spend, video, refresh
 */

import { slashCommandRegistry } from './registry'
import { formatCents, parseMoneyToCents, parsePeriod, resolveCampaign, transitionCampaign } from './utils'
import { serializeMention } from '@/lib/mentions/tokens'

// ─── Tier 1: campaign actions ────────────────────────────────────────────────

slashCommandRegistry.register({
  name: 'pause',
  aliases: ['p'],
  description: 'Pause an active campaign',
  tier: 'A',
  args: [{ name: 'campaign', type: 'campaign', required: true, description: 'Campaign name (fuzzy match)' }],
  handler: async (args, ctx) => {
    const query = String(args.campaign ?? '')
    const resolved = await resolveCampaign(query, ctx)
    if (!resolved.ok) return { kind: 'error', message: resolved.error }

    const result = await transitionCampaign(resolved.campaign.id, 'pause')
    if (!result.ok) return { kind: 'error', message: `Could not pause "${resolved.campaign.name}": ${result.error}` }

    return { kind: 'success', message: `Paused "${resolved.campaign.name}".` }
  },
})

slashCommandRegistry.register({
  name: 'resume',
  aliases: ['r'],
  description: 'Resume a paused campaign',
  tier: 'A',
  args: [{ name: 'campaign', type: 'campaign', required: true, description: 'Campaign name (fuzzy match)' }],
  handler: async (args, ctx) => {
    const query = String(args.campaign ?? '')
    const resolved = await resolveCampaign(query, ctx)
    if (!resolved.ok) return { kind: 'error', message: resolved.error }

    const result = await transitionCampaign(resolved.campaign.id, 'resume')
    if (!result.ok) return { kind: 'error', message: `Could not resume "${resolved.campaign.name}": ${result.error}` }

    return { kind: 'success', message: `Resumed "${resolved.campaign.name}".` }
  },
})

slashCommandRegistry.register({
  name: 'budget',
  aliases: ['b'],
  description: 'Update a campaign budget',
  tier: 'A',
  args: [
    { name: 'campaign', type: 'campaign', required: true, description: 'Campaign name' },
    { name: 'amount', type: 'amount', required: true, description: 'New budget (e.g. 5000, $5k)' },
  ],
  handler: async (args, ctx) => {
    const query = String(args.campaign ?? '')
    const cents = parseMoneyToCents(args.amount as string | number | undefined)
    if (cents == null || cents <= 0) {
      return { kind: 'error', message: `Invalid budget amount. Try "/budget <campaign> 5000" or "$5k".` }
    }

    const resolved = await resolveCampaign(query, ctx)
    if (!resolved.ok) return { kind: 'error', message: resolved.error }

    try {
      const res = await fetch(`/api/campaigns/${resolved.campaign.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ budget: cents }),
      })
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
      if (!res.ok) {
        const detail = typeof body.error === 'string' ? body.error : `HTTP ${res.status}`
        return { kind: 'error', message: `Could not update budget: ${detail}` }
      }
    } catch (err) {
      return { kind: 'error', message: err instanceof Error ? err.message : String(err) }
    }

    return {
      kind: 'success',
      message: `Updated "${resolved.campaign.name}" budget to ${formatCents(cents)}.`,
    }
  },
})

slashCommandRegistry.register({
  name: 'topup',
  description: 'Add funds to your wallet via Stripe Checkout',
  tier: 'A',
  args: [{ name: 'amount', type: 'amount', required: true, description: 'Deposit amount (min $1,000)' }],
  handler: async (args) => {
    const cents = parseMoneyToCents(args.amount as string | number | undefined)
    if (cents == null || cents < 100_000) {
      return { kind: 'error', message: 'Minimum deposit is $1,000. Try "/topup 1000" or "$5k".' }
    }
    if (cents > 10_000_000) {
      return { kind: 'error', message: 'Maximum deposit is $100,000. Reach out for larger amounts.' }
    }

    try {
      const res = await fetch('/api/wallet/deposit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount_cents: cents }),
      })
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
      if (!res.ok || typeof body.checkout_url !== 'string') {
        const detail = typeof body.error === 'string' ? body.error : `HTTP ${res.status}`
        return { kind: 'error', message: `Could not create checkout: ${detail}` }
      }

      return {
        kind: 'ui-action',
        action: { type: 'open-checkout', url: body.checkout_url },
        message: `Opening Stripe Checkout for ${formatCents(cents)}…`,
      }
    } catch (err) {
      return { kind: 'error', message: err instanceof Error ? err.message : String(err) }
    }
  },
})

slashCommandRegistry.register({
  name: 'launch',
  description: 'Launch a funded campaign (provisions SSP + activates)',
  tier: 'A',
  args: [{ name: 'campaign', type: 'campaign', required: true, description: 'Campaign name' }],
  handler: async (args, ctx) => {
    const query = String(args.campaign ?? '')
    const resolved = await resolveCampaign(query, ctx)
    if (!resolved.ok) return { kind: 'error', message: resolved.error }

    const result = await transitionCampaign(resolved.campaign.id, 'launch')
    if (!result.ok) return { kind: 'error', message: `Could not launch "${resolved.campaign.name}": ${result.error}` }

    return { kind: 'success', message: `Launched "${resolved.campaign.name}". It is now active.` }
  },
})

// ─── Tier 3: AI controls ─────────────────────────────────────────────────────

slashCommandRegistry.register({
  name: 'new',
  aliases: ['n'],
  description: 'Start a new chat thread',
  tier: 'A',
  args: [],
  handler: async () => ({
    kind: 'ui-action',
    action: { type: 'new-thread' },
    message: 'Starting a new thread…',
  }),
})

slashCommandRegistry.register({
  name: 'stop',
  description: 'Abort the current AI generation',
  tier: 'A',
  args: [],
  handler: async () => ({
    kind: 'ui-action',
    action: { type: 'stop-streaming' },
    message: 'Stopped.',
  }),
})

slashCommandRegistry.register({
  name: 'btw',
  description: 'Ask a side question without polluting the main thread',
  tier: 'A',
  args: [{ name: 'question', type: 'string', required: true, description: 'Your side question' }],
  handler: async (args, ctx) => {
    const question = String(args.question ?? '').trim()
    if (!question) return { kind: 'error', message: 'Ask a question after /btw.' }

    if (ctx.ui?.submitSideQuestion) {
      ctx.ui.submitSideQuestion(question)
      return { kind: 'success', message: `Side question sent.` }
    }

    // Fallback: route as a normal LLM call.
    return { kind: 'llm-routed', prompt: question }
  },
})

slashCommandRegistry.register({
  name: 'help',
  aliases: ['?'],
  description: 'Show available slash commands',
  tier: 'A',
  args: [{ name: 'command', type: 'string', required: false, description: 'Specific command name' }],
  handler: async (args) => {
    const target = String(args.command ?? '').replace(/^\//, '').trim().toLowerCase()
    return {
      kind: 'ui-action',
      action: target ? { type: 'open-help', commandName: target } : { type: 'open-help' },
    }
  },
})

// ─── Replace the stub /whoami with a real one ────────────────────────────────

slashCommandRegistry.register({
  name: 'whoami',
  description: 'Show your user, workspace, and role',
  tier: 'A',
  args: [],
  handler: async (_, ctx) => {
    const { data: { user } } = await ctx.supabase.auth.getUser()
    const email = user?.email ?? '(unknown email)'

    const { data: workspaceRow } = await ctx.supabase
      .from('workspaces')
      .select('name')
      .eq('id', ctx.workspaceId)
      .single()

    const { data: memberRow } = await ctx.supabase
      .from('workspace_members')
      .select('role')
      .eq('workspace_id', ctx.workspaceId)
      .eq('auth_user_id', ctx.userId)
      .maybeSingle()

    const workspaceName = (workspaceRow as { name?: string } | null)?.name ?? '(unknown workspace)'
    const role = (memberRow as { role?: string } | null)?.role ?? 'member'
    const adminBadge = ctx.isAdmin ? ' • Atrium admin' : ''

    return {
      kind: 'success',
      message: `${email} · ${workspaceName} · ${role}${adminBadge}`,
    }
  },
})

// ─── Tier 2: navigation ───────────────────────────────────────────────────────

type NavPage = { name: string; path: string }

const NAV_PAGES: NavPage[] = [
  { name: 'campaigns', path: '/campaigns' },
  // "dashboard" remains a long-standing user shortcut, but now lands on
  // the renamed Overview route.
  { name: 'dashboard', path: '/overview' },
  { name: 'videos', path: '/videos' },
  { name: 'brand', path: '/brand' },
  { name: 'activity', path: '/activity' },
  { name: 'settings', path: '/settings' },
  { name: 'usage', path: '/usage' },
  { name: 'chat', path: '/chat' },
  { name: 'connectors', path: '/connectors' },
]

function resolveNavPage(query: string): { ok: true; page: NavPage } | { ok: false; error: string } {
  const q = query.trim().toLowerCase()
  if (!q) return { ok: false, error: 'Provide a page name. Options: ' + NAV_PAGES.map(p => p.name).join(', ') }

  const exact = NAV_PAGES.filter(p => p.name === q)
  if (exact.length === 1) return { ok: true, page: exact[0] }

  const prefix = NAV_PAGES.filter(p => p.name.startsWith(q))
  if (prefix.length === 1) return { ok: true, page: prefix[0] }

  const substring = NAV_PAGES.filter(p => p.name.includes(q))
  if (substring.length === 1) return { ok: true, page: substring[0] }

  const candidates = (prefix.length > 1 ? prefix : substring.length > 1 ? substring : NAV_PAGES)
    .slice(0, 4)
    .map(p => p.name)
  return {
    ok: false,
    error: `No page matches "${query}". Did you mean: ${candidates.join(', ')}?`,
  }
}

const ADMIN_SUBSECTIONS = new Set([
  'overview', 'workspaces', 'campaigns', 'videos', 'sonobi', 'tag-manager',
  'finances', 'users', 'prompt-config', 'audit-log', 'connector-requests',
  'feedback', 'analytics', 'supply', 'invoices', 'pricing',
])

slashCommandRegistry.register({
  name: 'go',
  aliases: ['g'],
  description: 'Navigate to a top-level page (fuzzy match)',
  tier: 'A',
  args: [{ name: 'page', type: 'page', required: true, description: 'Page name (e.g. campaigns, brand, settings)' }],
  handler: async (args) => {
    const query = String(args.page ?? '')
    const resolved = resolveNavPage(query)
    if (!resolved.ok) return { kind: 'error', message: resolved.error }
    return { kind: 'navigate', path: resolved.page.path }
  },
})

slashCommandRegistry.register({
  name: 'campaign',
  description: 'Navigate to a campaign detail page',
  tier: 'A',
  args: [{ name: 'campaign', type: 'campaign', required: true, description: 'Campaign name (fuzzy match)' }],
  handler: async (args, ctx) => {
    const query = String(args.campaign ?? '')
    const resolved = await resolveCampaign(query, ctx)
    if (!resolved.ok) return { kind: 'error', message: resolved.error }
    return { kind: 'navigate', path: `/campaigns/${resolved.campaign.id}` }
  },
})

slashCommandRegistry.register({
  name: 'brand',
  description: 'Navigate to your brand kit',
  tier: 'A',
  // TODO: brand sub-sections (identity/product/competitor/asset) are managed via
  // client-side React state in the brand page, not URL params — deep-linking to a
  // sub-section is not supported yet. When/if the brand page learns to honor a
  // ?section=... query param (or a path segment), wire it up here.
  args: [{ name: 'section', type: 'string', required: false, description: 'Reserved — brand sub-sections not yet deep-linkable' }],
  handler: async () => ({ kind: 'navigate', path: '/brand' }),
})

slashCommandRegistry.register({
  name: 'admin',
  description: 'Navigate to the admin panel',
  tier: 'A',
  adminOnly: true,
  args: [{ name: 'section', type: 'string', required: false, description: 'Sub-section (e.g. users, finances, workspaces)' }],
  handler: async (args) => {
    const section = String(args.section ?? '').trim().toLowerCase()
    if (section && !ADMIN_SUBSECTIONS.has(section)) {
      const candidates = Array.from(ADMIN_SUBSECTIONS).filter(s => s.startsWith(section)).slice(0, 4)
      const hint = candidates.length ? ` Did you mean: ${candidates.join(', ')}?` : ''
      return { kind: 'error', message: `Unknown admin section "${section}".${hint}` }
    }
    return { kind: 'navigate', path: section ? `/admin/${section}` : '/admin' }
  },
})

// ─── Tier 4: LLM-routed workflow shortcuts ────────────────────────────────────

slashCommandRegistry.register({
  name: 'perf',
  description: 'Show campaign performance summary',
  tier: 'B',
  args: [
    { name: 'campaign', type: 'campaign', required: true, description: 'Campaign name' },
    { name: 'period', type: 'period', required: false, description: 'Period: today, week, month, all, 7d, 30d' },
  ],
  promptTemplate: (args) => {
    const campaign = String(args.campaign ?? '')
    const period = parsePeriod(args.period as string | undefined)
    return `Show performance for the ${campaign} campaign over ${period}.`
  },
  displayMessageBuilder: async (args, ctx) => {
    const res = await resolveCampaign(String(args.campaign ?? ''), ctx)
    if (!res.ok) return null
    const period = args.period ? ` ${parsePeriod(args.period as string | undefined)}` : ''
    return `perf ${serializeMention('campaign', res.campaign.id, res.campaign.name)}${period}`
  },
})

slashCommandRegistry.register({
  name: 'spend',
  description: 'Show spend breakdown by campaign',
  tier: 'B',
  args: [{ name: 'period', type: 'period', required: false, description: 'Period: today, week, month, all, 7d, 30d' }],
  promptTemplate: (args) => {
    const period = parsePeriod(args.period as string | undefined)
    return `Show my spend for ${period} broken down by campaign.`
  },
})

slashCommandRegistry.register({
  name: 'video',
  description: 'Generate a new video for a campaign',
  tier: 'B',
  args: [{ name: 'campaign', type: 'campaign', required: true, description: 'Campaign name' }],
  promptTemplate: (args) => {
    const campaign = String(args.campaign ?? '')
    return `Generate a new video for the ${campaign} campaign using my brand kit.`
  },
  displayMessageBuilder: async (args, ctx) => {
    const res = await resolveCampaign(String(args.campaign ?? ''), ctx)
    if (!res.ok) return null
    return `video ${serializeMention('campaign', res.campaign.id, res.campaign.name)}`
  },
})

slashCommandRegistry.register({
  name: 'refresh',
  description: 'Trigger a creative refresh for a campaign',
  tier: 'B',
  args: [{ name: 'campaign', type: 'campaign', required: true, description: 'Campaign name' }],
  promptTemplate: (args) => {
    const campaign = String(args.campaign ?? '')
    return `Trigger a creative refresh for the ${campaign} campaign.`
  },
  displayMessageBuilder: async (args, ctx) => {
    const res = await resolveCampaign(String(args.campaign ?? ''), ctx)
    if (!res.ok) return null
    return `refresh ${serializeMention('campaign', res.campaign.id, res.campaign.name)}`
  },
})

slashCommandRegistry.register({
  name: 'measurement',
  description: 'Set up Atrium measurement for the current brand',
  tier: 'B',
  args: [],
  promptTemplate: () => 'Set up measurement for the current brand. Use setup_measurement and return the install snippet.',
})

slashCommandRegistry.register({
  name: 'recommend-events',
  description: 'Get AI-recommended conversion events for the current brand',
  tier: 'B',
  args: [],
  promptTemplate: () => 'Recommend the conversion events I should track for the current brand. Use recommend_events_for_brand and explain the rationale for the top picks.',
})

slashCommandRegistry.register({
  name: 'install-snippet',
  description: 'Get the Atrium install snippet for the current brand',
  tier: 'B',
  args: [],
  promptTemplate: () => 'Get the install snippet for the current brand. Ask for framework only if it is not clear.',
})

slashCommandRegistry.register({
  name: 'verify-pixel',
  description: 'Verify whether the Atrium pixel is installed and firing',
  tier: 'B',
  args: [],
  promptTemplate: () => 'Verify whether the Atrium pixel is installed and firing for the current brand.',
})

slashCommandRegistry.register({
  name: 'health-check',
  description: 'Check pixel health: detect volume anomalies, stalled events, and quality issues',
  tier: 'B',
  args: [],
  promptTemplate: () => 'Run a pixel health check on the current brand. Use analyze_pixel_health and surface any critical or warning alerts.',
})
